import { lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { BASE_URL } from './data/routes';
import { Navigation } from './components/Navigation';
import { Hero } from './components/Hero';
import { ToolsGrid } from './components/ToolsGrid';
import { StatsBoard } from './components/StatsBoard';
import { LatestArticles } from './components/LatestArticles';
import { Footer } from './components/Footer';

// Keep the homepage in the entry chunk and load the larger secondary pages only
// when a visitor navigates to them.
const BillCalculator = lazy(() => import('./components/tools/BillCalculator').then(({ BillCalculator }) => ({ default: BillCalculator })));
const SolarSizing = lazy(() => import('./components/tools/SolarSizing').then(({ SolarSizing }) => ({ default: SolarSizing })));
const PowerManagerGame = lazy(() => import('./components/tools/PowerManagerGame').then(({ PowerManagerGame }) => ({ default: PowerManagerGame })));
const TariffChecker = lazy(() => import('./components/tools/TariffChecker').then(({ TariffChecker }) => ({ default: TariffChecker })));
const OutageCostCalculator = lazy(() => import('./components/tools/OutageCostCalculator').then(({ OutageCostCalculator }) => ({ default: OutageCostCalculator })));
const EnergyAudit = lazy(() => import('./components/tools/EnergyAudit').then(({ EnergyAudit }) => ({ default: EnergyAudit })));
const RefineryTracker = lazy(() => import('./components/tools/RefineryTracker').then(({ RefineryTracker }) => ({ default: RefineryTracker })));
const DiscoDirectory = lazy(() => import('./components/tools/DiscoDirectory').then(({ DiscoDirectory }) => ({ default: DiscoDirectory })));
const ArticlePage = lazy(() => import('./components/ArticlePage').then(({ ArticlePage }) => ({ default: ArticlePage })));
const GridsPage = lazy(() => import('./components/GridsPage').then(({ GridsPage }) => ({ default: GridsPage })));
const RenewablesPage = lazy(() => import('./components/RenewablesPage').then(({ RenewablesPage }) => ({ default: RenewablesPage })));
const OilGasPage = lazy(() => import('./components/OilGasPage').then(({ OilGasPage }) => ({ default: OilGasPage })));
const ToolsPage = lazy(() => import('./components/ToolsPage').then(({ ToolsPage }) => ({ default: ToolsPage })));
const AboutPage = lazy(() => import('./components/AboutPage').then(({ AboutPage }) => ({ default: AboutPage })));
const ContactPage = lazy(() => import('./components/ContactPage').then(({ ContactPage }) => ({ default: ContactPage })));
const DataSourcesPage = lazy(() => import('./components/DataSourcesPage').then(({ DataSourcesPage }) => ({ default: DataSourcesPage })));
const PrivacyPolicyPage = lazy(() => import('./components/PrivacyPolicyPage').then(({ PrivacyPolicyPage }) => ({ default: PrivacyPolicyPage })));
const TermsOfUsePage = lazy(() => import('./components/TermsOfUsePage').then(({ TermsOfUsePage }) => ({ default: TermsOfUsePage })));
const NotFoundPage = lazy(() => import('./components/NotFoundPage').then(({ NotFoundPage }) => ({ default: NotFoundPage })));
const SolarQuotesPage = lazy(() => import('./components/SolarQuotesPage').then(({ SolarQuotesPage }) => ({ default: SolarQuotesPage })));

export async function preloadRouteModules() {
  await Promise.all([
    import('./components/tools/BillCalculator'),
    import('./components/tools/SolarSizing'),
    import('./components/tools/PowerManagerGame'),
    import('./components/tools/TariffChecker'),
    import('./components/tools/OutageCostCalculator'),
    import('./components/tools/EnergyAudit'),
    import('./components/tools/RefineryTracker'),
    import('./components/tools/DiscoDirectory'),
    import('./components/ArticlePage'),
    import('./components/GridsPage'),
    import('./components/RenewablesPage'),
    import('./components/OilGasPage'),
    import('./components/ToolsPage'),
    import('./components/AboutPage'),
    import('./components/ContactPage'),
    import('./components/DataSourcesPage'),
    import('./components/PrivacyPolicyPage'),
    import('./components/TermsOfUsePage'),
    import('./components/NotFoundPage'),
    import('./components/SolarQuotesPage'),
  ]);
}

export type Page =
  | 'home'
  | 'grids'
  | 'renewables'
  | 'oil-gas'
  | 'tools'
  | 'bill-calculator'
  | 'solar-sizing'
  | 'power-manager'
  | 'tariff-checker'
  | 'outage-calculator'
  | 'energy-audit'
  | 'refinery-tracker'
  | 'disco-directory'
  | 'articles'
  | 'solar-quotes'
  | 'about'
  | 'contact'
  | 'data-sources'
  | 'privacy-policy'
  | 'terms-of-use';


const HomePage = () => {
  return (
    <>
      <Helmet>
        <link rel="canonical" href="https://power.ng/" />
      </Helmet>
      <Hero />
      <ToolsGrid />
      <StatsBoard />
      <LatestArticles />
    </>
  );
}

export default function App() {
  const siteSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': `${BASE_URL}/#organization`,
        name: 'Power.ng',
        url: `${BASE_URL}/`,
        logo: `${BASE_URL}/web-app-manifest-512x512.png`,
      },
      {
        '@type': 'WebSite',
        '@id': `${BASE_URL}/#website`,
        url: `${BASE_URL}/`,
        name: 'Power.ng',
        publisher: { '@id': `${BASE_URL}/#organization` },
        potentialAction: {
          '@type': 'SearchAction',
          target: `${BASE_URL}/articles?search={search_term_string}`,
          'query-input': 'required name=search_term_string',
        },
      },
    ],
  };

  return (

    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Power.ng - Nigeria's Energy Information Platform</title>
        <meta name="description" content="Detailed analysis, tools, and data about Nigeria's power and energy sector. Calculate bills, track renewables, and explore the grid." />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://power.ng/" />
        <meta property="og:title" content="Power.ng - Nigeria's Energy Information Platform" />
        <meta property="og:description" content="Detailed analysis, tools, and data about Nigeria's power and energy sector." />
        <meta property="og:image" content="/og-image.png" />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://power.ng/" />
        <meta property="twitter:title" content="Power.ng - Nigeria's Energy Information Platform" />
        <meta property="twitter:description" content="Detailed analysis, tools, and data about Nigeria's power and energy sector." />
        <meta property="twitter:image" content="/og-image.png" />
        <script type="application/ld+json">{JSON.stringify(siteSchema)}</script>
      </Helmet>
      <Navigation />
      <main>
        <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/grids" element={<GridsPage />} />
            <Route path="/renewables" element={<RenewablesPage />} />
            <Route path="/oil-gas" element={<OilGasPage />} />
            <Route path="/tools" element={<ToolsPage />} />

            <Route path="/tools/bill-calculator" element={<BillCalculator />} />
            <Route path="/tools/solar-sizing" element={<SolarSizing />} />
            <Route path="/power-manager" element={<PowerManagerGame />} />
            <Route path="/tools/tariff-checker" element={<TariffChecker />} />
            <Route path="/tools/outage-calculator" element={<OutageCostCalculator />} />
            <Route path="/tools/energy-audit" element={<EnergyAudit />} />
            <Route path="/tools/refinery-tracker" element={<RefineryTracker />} />
            <Route path="/tools/disco-directory" element={<DiscoDirectory />} />

            <Route path="/articles" element={<ArticlePage />} />
            <Route path="/articles/:slug" element={<ArticlePage />} />
            <Route path="/solar-quotes" element={<SolarQuotesPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/data-sources" element={<DataSourcesPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/terms-of-use" element={<TermsOfUsePage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
    </div>
  );
}
